import type { PropsWithChildren } from "react";
import { useMemo, useState } from "react";

import { VStack } from "@/components/Layout";
import { getNumberScale } from "@/utils/number/scale";

import { CompoundChartCore } from "./CompoundChartCore";
import { CompoundChartContext } from "./context";
import type { CompoundChartDatum } from "./types";

export { CompoundChartCore } from "./CompoundChartCore";
export { CompoundChartLegend } from "./CompoundChartLegend";
export { CompoundChartTooltip } from "./CompoundChartTooltip";
export { useCompoundChartContext } from "./context";
export type { CompoundChartDatum } from "./types";

type Props = PropsWithChildren<{ data: CompoundChartDatum[] }>;

export function CompoundChart({ data, children }: Props) {
  const [hoveredDatum, setHoveredDatum] = useState<CompoundChartDatum>();

  const scales = useMemo(() => {
    const maxValue = Math.max(...data.map((d) => d.value), 100);
    const maxCumulative = Math.max(...data.map((d) => d.cumulative), 1000);

    const valueScale = getNumberScale(maxValue);
    const cumulativeScale = getNumberScale(maxCumulative);

    return { valueScale, cumulativeScale };
  }, [data]);

  return (
    <VStack className="relative w-full h-full gap-4">
      <CompoundChartContext.Provider
        value={{
          scales,
          data,
          hoveredDatum,
          onHoverDatumChange: setHoveredDatum,
        }}
      >
        <div className="flex-1 min-h-0">
          <CompoundChartCore />
        </div>
        <div className="shrink-0">{children}</div>
      </CompoundChartContext.Provider>
    </VStack>
  );
}
